import { useEditEventModal } from "@circle-react/components/Modals/EditEventModal";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { reactQueryPut } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Event } from "@circle-react/types/Event";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

const DISPLAY_EDIT_MODAL_DELAY_MILLISECONDS = 500;

interface DuplicatedEventParams {
  space_id?: number;
}

export const useEventDuplicateMutation = (
  event: Pick<Event, "id" | "slug" | "space_id">,
) => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const {
    helpers: { findById: findSpace },
  } = useSpacesContext();
  const editEventModal = useEditEventModal();

  return useMutation<Event, Error, DuplicatedEventParams>({
    mutationFn: (data: DuplicatedEventParams) =>
      reactQueryPut<Event, { event: { event_id: number; space_id?: number } }>(
        internalApi.events.duplicate({
          spaceId: event.space_id,
          slug: event.slug,
        }),
        { event: { event_id: event.id, space_id: data.space_id } },
      ),
    onSuccess: duplicatedEvent => {
      const eventSpace = findSpace(duplicatedEvent.space_id);
      if (!eventSpace) {
        error(t("space_not_found"));
        return;
      }
      void navigate({
        to: "/c/$spaceSlug/$postSlug",
        params: {
          spaceSlug: eventSpace.slug,
          postSlug: duplicatedEvent.slug,
        },
      });
      setTimeout(
        () => void editEventModal.show({ event: duplicatedEvent }),
        DISPLAY_EDIT_MODAL_DELAY_MILLISECONDS,
      );
      success(t("event_duplicated_successfully"));
    },
    onError: () => {
      error(t("event_duplication_failed"));
    },
  });
};
